/**
 * Types for Create Liquidation Address Wizard
 * 
 * @module addresses/wizard
 */

export type DestinationType = 'fiat' | 'crypto' | 'wallet';

// Note: Only assets with available icons in /public/crypto-icons/color/
export type CryptoAsset =
  | 'USDC'
  | 'USDT'
  | 'EURC'
  | 'PYUSD'
  | 'DAI'
  | 'ETH';

export type BlockchainNetwork =
  | 'ethereum'
  | 'polygon'
  | 'arbitrum'
  | 'base'
  | 'solana'
  | 'avalanche'
  | 'optimism'
  | 'stellar'
  | 'tron';

export type PaymentRail = 'ach' | 'sepa' | 'wire' | 'spei';

export type FiatCurrency = 'USD' | 'EUR' | 'MXN';

export interface WizardFormData {
  // Step 1: Destination Type
  destinationType: DestinationType;

  // Step 2: Asset & Network
  asset: CryptoAsset;
  network: BlockchainNetwork;

  // Step 3a: Fiat Destination
  externalAccountId?: string;
  paymentRail?: PaymentRail;
  currency?: FiatCurrency;

  // Step 3b: Crypto Destination
  destinationAddress?: string;
  destinationChain?: BlockchainNetwork;
  destinationAsset?: CryptoAsset;

  // Step 3c: Wallet Destination
  walletId?: string;

  // Optional
  label?: string;
  returnAddress?: string;
}

export interface WizardStep {
  id: number;
  title: string;
  description: string;
  isComplete: boolean;
  isActive: boolean;
}

export interface BankAccount {
  id: string;
  externalAccountId: string;
  bankName: string | null;
  accountNumberMasked: string;
  routingNumberMasked: string;
  currency: string | null;
  accountType: string | null;
  status: string;
  country: string | null;
}

export interface PayoutRoute {
  id: string;
  externalAccountId: string;
  paymentRail: PaymentRail;
  currency: FiatCurrency;
  bankName?: string;
  accountNumberMasked: string;
}

// Asset-Network compatibility matrix (only assets with available icons)
export const ASSET_NETWORK_COMPATIBILITY: Record<CryptoAsset, BlockchainNetwork[]> = {
  'USDC': ['ethereum', 'polygon', 'arbitrum', 'base', 'solana', 'avalanche', 'optimism', 'stellar'],
  'USDT': ['ethereum', 'tron'],
  'EURC': ['base', 'solana'],
  'PYUSD': ['ethereum', 'solana'],
  'DAI': ['ethereum'],
  'ETH': ['ethereum'],
};

// Network display names
export const NETWORK_LABELS: Record<BlockchainNetwork, string> = {
  'ethereum': 'Ethereum',
  'polygon': 'Polygon',
  'arbitrum': 'Arbitrum',
  'base': 'Base',
  'solana': 'Solana',
  'avalanche': 'Avalanche',
  'optimism': 'Optimism',
  'stellar': 'Stellar',
  'tron': 'Tron',
};

// Asset display info (only assets with available icons)
export const ASSET_INFO: Record<CryptoAsset, { name: string; description: string }> = {
  'USDC': { name: 'USDC', description: 'USD Coin - Most popular stablecoin' },
  'USDT': { name: 'USDT', description: 'Tether - High liquidity stablecoin' },
  'EURC': { name: 'EURC', description: 'Euro Coin - Euro-pegged stablecoin' },
  'PYUSD': { name: 'PYUSD', description: 'PayPal USD - PayPal stablecoin' },
  'DAI': { name: 'DAI', description: 'DAI - Decentralized stablecoin' },
  'ETH': { name: 'ETH', description: 'Ethereum - Native token' },
};

// Payment rail info
export const PAYMENT_RAIL_INFO: Record<PaymentRail, { name: string; region: string; speed: string }> = {
  'ach': { name: 'ACH', region: 'United States', speed: '1-2 business days' },
  'sepa': { name: 'SEPA', region: 'Europe', speed: '1 business day' },
  'wire': { name: 'Wire', region: 'United States', speed: 'Same day' },
  'spei': { name: 'SPEI', region: 'Mexico', speed: 'Same day' },
};

// Default values
export const DEFAULT_FORM_DATA: WizardFormData = {
  destinationType: 'fiat',
  asset: 'USDC',
  network: 'ethereum',
  currency: 'USD',
  paymentRail: 'ach',
};

